import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useEvents, useNow } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { inr } from '../lib/format';
import type { Sitting } from '../lib/types';
import { ResidentBar } from '../components/resident';
import { ErrorNote, Spinner } from '../components/ui';
import { bookByText, type Cart, CartLines, cartSummary, MenuPicker, useMenu } from '../components/MenuPicker';

const CART_KEY = 'one10:cart';

function loadCart(): Cart {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || '{}') as Cart; } catch { return {}; }
}

/**
 * Meals of the live events, open to anyone: signing in (and saying which flat) is only asked for at "Place order".
 * The chosen meals stay in this browser meanwhile, and the review opens again once they're back.
 */
export default function Book() {
  const ev = useEvents();
  const { session, me, meLoading } = useAuth();
  const [params] = useSearchParams();
  const guest = !session;
  const noFamily = !!session && !meLoading && !me?.flat;
  const [eventId, setEventId] = useState<string | null>(params.get('event'));
  // Events with meals to book (one published before its meals has none yet). The event to book: the one picked, else
  // the first taking bookings.
  const withMeals = ev.live.filter((e) => ev.current.some((s) => s.event_id === e.id));
  const chosen = withMeals.find((e) => e.id === eventId) ?? withMeals.find((e) => e.booking_open) ?? withMeals[0] ?? null;
  const soon = ev.live.filter((e) => !withMeals.includes(e));
  const m = useMenu(chosen?.id ?? null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const now = useNow(60_000);
  const [cart, setCart] = useState<Cart>(loadCart);
  const [reviewing, setReviewing] = useState(params.get('review') === '1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* storage unavailable */ }
  }, [cart]);

  const isClosed = (s: Sitting) => now >= new Date(s.booking_closes_at).getTime();
  const bookingOpen = m.settings.data?.booking_open !== false && chosen?.booking_open !== false;
  const { lines, total, plates, payload } = cartSummary(cart, m, isClosed);

  async function place() {
    // Not signed in, or no family yet: do that first, then come back to this review with the same meals.
    const back = `/book?review=1${chosen ? `&event=${encodeURIComponent(chosen.id)}` : ''}`;
    if (guest) return navigate(`/signin?next=${encodeURIComponent(back)}`);
    if (noFamily) return navigate(`/link?next=${encodeURIComponent(back)}`);
    setError(null);
    setBusy(true);
    try {
      const order = await api.placeOrder(payload);
      setCart({});
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['bhog'] });
      navigate(`/orders/${encodeURIComponent(order.code)}`, { replace: true });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (ev.ready && !chosen) {
    return (
      <div className="shell">
        <ResidentBar back={{ to: '/', label: guest ? 'Home' : 'My passes' }} title="Book meals" />
        <main className="app-body">
          <div className="card empty">
            <p className="display" style={{ fontSize: 20 }}>{soon.length ? `Food booking for ${soon.map((e) => e.name).join(' and ')} opens soon` : 'No event is open for booking'}</p>
            <p>The Food Committee opens booking for each event. Your passes from past events are still on My passes.</p>
            {soon.map((e) => <Link key={e.id} className="btn ghost" to={`/e/${encodeURIComponent(e.id)}`}>{e.name}: programme ›</Link>)}
          </div>
        </main>
      </div>
    );
  }
  if (m.isPending) {
    return <div className="shell"><ResidentBar back={{ to: '/', label: guest ? 'Home' : 'My passes' }} title="Book meals" /><div className="empty"><Spinner /></div></div>;
  }
  if (m.error) {
    return <div className="shell"><ResidentBar back={{ to: '/', label: guest ? 'Home' : 'My passes' }} title="Book meals" /><p className="err app-body">{errorText(m.error)}</p></div>;
  }

  if (reviewing && lines.length > 0) {
    return (
      <div className="shell">
        <div className="app-bar slim">
          <button className="link-btn" onClick={() => setReviewing(false)}>‹ Change meals</button>
          <span className="app-sub">Review order</span>
        </div>
        <main className="app-body">
          <ol className="msteps"><li className="done">Your meals</li><li>Pay</li><li>Confirmed</li></ol>
          <h1 className="display" style={{ fontSize: 24 }}>Review your order</h1>
          <div className="card">
            <CartLines lines={lines} m={m} />
            <div className="ototal"><span>{plates} plate{plates === 1 ? '' : 's'}</span><span>Total <b>{inr(total)}</b></span></div>
          </div>
          {guest || noFamily ? (
            <p className="fine">
              {guest ? "Next you sign in with your email (we email you a code) and say which flat you're in" : "Next you say which flat you're in"}.
              Your meals stay chosen, and then you {total === 0 ? 'confirm the seats' : 'choose how to pay'}.
            </p>
          ) : <p className="fine">{total === 0 ? 'Nothing to pay. Your seats are confirmed straight away.'
            : m.settings.data?.online_payments
              ? 'Next you choose how to pay: online (your passes are confirmed at once), by UPI with the 12-digit UTR, or in cash at the help desk. We email the passes to you.'
              : 'Next you pay by UPI and enter the 12-digit UTR. Your meal passes unlock when the Food Committee confirms the payment, and we email them to you.'}</p>}
          <ErrorNote error={error} />
          <button className="btn primary block lg" disabled={busy || lines.length === 0 || (!!session && meLoading)} onClick={() => void place()}>
            {busy ? 'Placing order…' : guest ? `Sign in to book · ${inr(total)}` : noFamily ? 'Continue' : total > 0 ? `Place order · ${inr(total)}` : 'Confirm seats'}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <ResidentBar back={{ to: '/', label: guest ? 'Home' : 'My passes' }} title="Book meals" />
      <main className="app-body" style={{ paddingBottom: 24 }}>
        {ev.live.length > 1 ? (
          <div className="fchips" role="tablist" aria-label="Events">
            {ev.live.map((e) => (
              <button key={e.id} type="button" role="tab" className="fchip" aria-pressed={e.id === chosen?.id}
                onClick={() => { setEventId(e.id); }}>{e.name}</button>
            ))}
          </div>
        ) : chosen && <h1 className="display" style={{ fontSize: 22 }}>{chosen.name}</h1>}
        {guest && (
          <p className="fine">You're looking without signing in. Pick your meals; you sign in only when you place the order.</p>
        )}
        {!bookingOpen && <div className="notice warn"><b>Booking is closed.</b> The Food Committee has paused new orders{ev.live.length > 1 ? ' for this event' : ''}.</div>}
        <MenuPicker m={m} cart={cart} onChange={setCart} isClosed={isClosed} disabled={!bookingOpen} guest={guest || noFamily}
          closedText={(s) => bookByText(s, isClosed(s))} />
      </main>
      <div className="cartbar">
        <div className="cartbar-sum">
          <b>{inr(total)}</b>
          <span>{lines.length ? `${lines.length} meal${lines.length === 1 ? '' : 's'} · ${plates} plates` : 'No meals chosen'}</span>
        </div>
        <button className="btn primary" disabled={!lines.length || !bookingOpen} onClick={() => setReviewing(true)}>Review order</button>
      </div>
    </div>
  );
}
